/**
 * PLP 위시리스트 — 추가되는 variant 를 "품절 제외 첫 available variant" 로 강제 (Option B: click 가로채기)
 *
 * 🔴 [중요] 회귀 case: wiki/docs/wishlist-sold-out-variant-trap.md (작성 예정)
 *
 * 문제:
 *   Swym SDK 가 PLP 카드 wishlist 버튼에 렌더한 container 는
 *   `data-product-id="/products/<handle>"` 만 갖고 variant 정보 없음. Swym 이 fetch 한
 *   product.variants 의 [0] 을 무조건 사용 → 그 사이즈가 품절이면 PLP 엔 안 보이지만
 *   wishlist 엔 품절 variant 로 담김.
 *
 * 시도 이력:
 *   - Option A: container.dataset.productId 에 `?variant=<id>` 붙이기 → Swym 이 URL 파라미터 무시.
 *     2026-05-24 검증: stored epi 가 여전히 variants[0] 로 들어감. 폐기.
 *   - Option B (현재): click 가로채기 + `_swat.api.addToWishList` 직접 호출.
 *     2026-05-24 검증: stored epi = 우리가 지정한 first-available variant 로 정상 저장.
 *
 * 동작:
 *   capture-phase click 으로 Swym 자체 핸들러 + wishlist-open-on-add.js 가로채기.
 *   add (aria-pressed=false) + remove (aria-pressed=true) 모두 우리가 처리 — Swym 은 우리가
 *   직접 호출한 addToWishList 의 상태를 자체적으로 트래킹 못 함 (Swym 의 click → API 경로 우회).
 *   add 시점: product fetch → first-available variant 선택 → _swat.api.addToWishList(payload)
 *   remove 시점: fetch 현재 wishlist → 같은 empi 인 item enum → removeFromWishList(item)
 *   성공 시 aria-pressed 토글 + 배지 카운트 갱신 + (add 시) drawer 자동 open.
 *
 * 폴백:
 *   - 모든 variant 가 sold-out 인 경우 variants[0] 사용 (현 동작 유지)
 *   - fetch 실패 / _swat.api 미준비 / 예외 등 → Swym 디폴트 동작 유지 (가로채기 무효)
 *
 * 가드:
 *   - dataset.variantPickerBusy 플래그로 동시 클릭 방지
 *   - product fetch 결과 in-memory 캐시 (같은 handle 재호출 X)
 *   - 모든 호출 try/catch — 실패 시 silent fallback
 */
(function () {
  'use strict';

  const BTN_SELECTOR = '.swym-advanced-wishlist-collections';
  const CONTAINER_SELECTOR = '.swym-advanced-wishlist-collections-container';
  const DRAWER_OPEN_DELAY_MS = 350;

  const productCache = new Map();
  const inflight = new Map();

  // 🔴 현재 위시리스트 handle 집합 = 하트 표시의 단일 기준. reconcile()(서버 api.fetch)로 재구성,
  //    직접 클릭(handleAdd/Remove)으로 낙관적 갱신. applyHearts 가 이 집합 기준으로 하트를 양방향 정합.
  let wishlistHandles = new Set();

  function extractHandle(productId) {
    const m = (productId || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  async function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    if (inflight.has(handle)) return inflight.get(handle);
    const p = (async () => {
      try {
        const res = await fetch(MISTO.localeUrl(`/products/${encodeURIComponent(handle)}.js`));
        if (!res.ok) return null;
        const data = await res.json();
        productCache.set(handle, data);
        return data;
      } catch (e) {
        return null;
      } finally {
        inflight.delete(handle);
      }
    })();
    inflight.set(handle, p);
    return p;
  }

  function pickVariant(product) {
    if (!product || !Array.isArray(product.variants) || !product.variants.length) return null;
    return product.variants.find((v) => v.available) || product.variants[0];
  }

  // 🔴 배지 = Swym 권위값으로 re-sync (수동 increment 금지).
  //    이전 bumpBadge(±delta) 는 Swym 자체 배지 갱신 위에 또 더해 순간 +2 회귀 유발
  //    (담기 → Swym 이 배지 1 로 올림 + bumpBadge(1) 이 또 +1 → 2, 새로고침 시 Swym 진짜값 1 로 복귀).
  //    fetch 성공 콜백에서만 set → 실패/타임아웃 시 0 으로 안 떨어지고 Swym 값 유지.
  function syncBadge() {
    const badge = document.querySelector('.swym-anchor-badge');
    if (!badge) return;
    const api = window._swat && window._swat.api;
    if (!api || typeof api.fetch !== 'function') return;
    try {
      api.fetch(
        function (items) {
          const count = Array.isArray(items) ? items.length : 0;
          badge.dataset.count = String(count);
          badge.textContent = String(count);
        },
        function () {}
      );
    } catch (e) {}
  }

  function openDrawer() {
    const anchor = document.querySelector('#notepad-anchor-title, #swym-anchor a, #swym-plugin button');
    if (anchor) {
      try { anchor.click(); } catch (e) {}
    }
  }

  function addToWishlist(payload) {
    return new Promise((resolve) => {
      const api = window._swat && window._swat.api;
      if (!api || typeof api.addToWishList !== 'function') {
        resolve({ ok: false, reason: 'api-not-ready' });
        return;
      }
      try {
        api.addToWishList(
          payload,
          () => resolve({ ok: true }),
          (err) => resolve({ ok: false, err: String(err) })
        );
        setTimeout(() => resolve({ ok: false, timeout: true }), 5000);
      } catch (e) {
        resolve({ ok: false, exception: String(e) });
      }
    });
  }

  function fetchWishlist() {
    return new Promise((resolve) => {
      const api = window._swat && window._swat.api;
      if (!api || typeof api.fetch !== 'function') {
        resolve([]);
        return;
      }
      try {
        api.fetch((items) => resolve(items || []), () => resolve([]));
        setTimeout(() => resolve([]), 4000);
      } catch (e) {
        resolve([]);
      }
    });
  }

  function removeFromWishlist(item) {
    return new Promise((resolve) => {
      const api = window._swat && window._swat.api;
      if (!api || typeof api.removeFromWishList !== 'function') {
        resolve(false);
        return;
      }
      try {
        api.removeFromWishList(item, () => resolve(true), () => resolve(false));
        setTimeout(() => resolve(false), 4000);
      } catch (e) {
        resolve(false);
      }
    });
  }

  async function handleAdd(btn, container) {
    if (container.dataset.variantPickerBusy === '1') return;
    container.dataset.variantPickerBusy = '1';

    try {
      const handle = extractHandle(container.dataset.productId);
      if (!handle) return;
      const product = await fetchProduct(handle);
      const variant = pickVariant(product);
      if (!product || !variant) return;

      const payload = {
        empi: product.id,
        epi: variant.id,
        du: location.origin + '/products/' + handle,
        dt: product.title,
        iu: product.featured_image,
        pr: variant.price / 100,
        bt: product.vendor,
      };

      const result = await addToWishlist(payload);
      if (!result.ok) {
        console.warn('[wishlist-variant-picker] add failed:', result);
        return;
      }

      btn.setAttribute('aria-pressed', 'true');
      wishlistHandles.add(handle);
      syncBadge();
      setTimeout(openDrawer, DRAWER_OPEN_DELAY_MS);
    } finally {
      container.dataset.variantPickerBusy = '';
    }
  }

  async function handleRemove(btn, container) {
    if (container.dataset.variantPickerBusy === '1') return;
    container.dataset.variantPickerBusy = '1';

    try {
      const handle = extractHandle(container.dataset.productId);
      if (!handle) return;
      const product = await fetchProduct(handle);
      if (!product) return;
      const empi = product.id;

      const items = await fetchWishlist();
      const matched = items.filter((i) => i.empi === empi);
      if (!matched.length) {
        wishlistHandles.delete(handle);
        btn.setAttribute('aria-pressed', 'false');
        return;
      }

      let removed = 0;
      for (const it of matched) {
        if (await removeFromWishlist(it)) removed++;
      }
      if (removed > 0) {
        wishlistHandles.delete(handle);
        btn.setAttribute('aria-pressed', 'false');
        syncBadge();
      }
    } finally {
      container.dataset.variantPickerBusy = '';
    }
  }

  // 🔴 하트 표시 = wishlistHandles 단일 기준, 양방향 정합(있으면 on, 없으면 off).
  //    PLP(.swym-advanced-wishlist-collections) = aria-pressed (Swym CSS).
  //    PDP 커스텀버튼(#swym-custom-pdp-button) = SVG path fill(none↔currentColor) + aria.
  const PDP_BTN_SELECTOR = '#swym-custom-pdp-button button';

  function applyHearts() {
    document.querySelectorAll(CONTAINER_SELECTOR).forEach((c) => {
      const h = extractHandle(c.dataset.productId);
      if (!h) return;
      const b = c.querySelector(BTN_SELECTOR);
      if (b) b.setAttribute('aria-pressed', wishlistHandles.has(h) ? 'true' : 'false');
    });
    const ph = extractHandle(location.pathname);
    if (ph) {
      const b = document.querySelector(PDP_BTN_SELECTOR);
      if (b) {
        const on = wishlistHandles.has(ph);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        b.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Add to wishlist');
        const p = b.querySelector('svg path');
        if (p) p.setAttribute('fill', on ? 'currentColor' : 'none');
      }
    }
  }

  // 🔴 [중요] 권위적 재조회 — wishlistHandles 를 Swym 서버(api.fetch) 기준으로 재구성 후 하트 정합.
  //    회귀 case (2026-06-11) docs/claude/design-system.md: 드로어에서 1개 삭제 시 Swym 이
  //    sw:removedfromwishlist 를 *엉뚱한 상품*(우리가 _swat.api.addToWishList 로 직접 담아 Swym notepad 가
  //    추적 못 하는 최근 항목)에 대해 발화 → 이벤트 du 를 믿고 끄면 최근 추가 하트가 무더기로 빠지고(cascade)
  //    정작 삭제한 항목 하트는 남음(stale-on). 서버 데이터는 항상 정상(새로고침 복원).
  //    🔴 따라서 이벤트 du 신뢰 금지. 이벤트는 "변경됨" 트리거로만 쓰고, on/off 는 항상 서버 재조회로만 판단.
  //    디바운스로 (1)이벤트 다중 발화 흡수 (2)서버 캐시 settle 대기(이벤트 시점 fetch 는 lag 으로 stale 가능).
  let observerStarted = false;
  function startObserver() {
    if (observerStarted) return;
    observerStarted = true;
    // Swym 은 버튼을 SDK ready 이후 비동기/lazy-load 로 주입 → 추가되는 카드에도 현재 집합 재적용.
    let scheduled = false;
    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () { scheduled = false; applyHearts(); });
    }).observe(document.body, { childList: true, subtree: true });
  }

  function reconcile() {
    const api = window._swat && window._swat.api;
    if (!api || typeof api.fetch !== 'function') return;
    try {
      api.fetch(function (items) {
        if (!Array.isArray(items)) return; // 이상 응답 → 유지 (전체 off 방지)
        wishlistHandles = new Set(items.map((i) => extractHandle(i && i.du)).filter(Boolean));
        applyHearts();
        startObserver(); // 첫 성공 재조회 후 observer 가동 (로드 시 빈 집합으로 깜빡 off 방지)
      }, function () {});
    } catch (e) {}
  }

  let resyncTimer = null;
  function scheduleReconcile() {
    if (resyncTimer) clearTimeout(resyncTimer);
    resyncTimer = setTimeout(reconcile, 700);
  }

  // 로드 시 서버 기준 1회 정합 (이후 카드 주입은 startObserver 의 MutationObserver 가 커버)
  function hydrate() {
    reconcile();
  }

  // 드로어/타페이지의 위시리스트 변경 → 디바운스 후 서버 재조회.
  //    🔴 이벤트 du 를 직접 반영하지 않는다 (Swym 이 엉뚱한 du 로 발화 → cascade/stale-on 회귀 방지).
  function bindWishlistEvents() {
    const ev = window._swat && window._swat.evtLayer;
    if (!ev || typeof ev.addEventListener !== 'function' || ev.__mistoWishlistResyncBound) return;
    ev.__mistoWishlistResyncBound = true;
    ev.addEventListener('sw:removedfromwishlist', scheduleReconcile);
    ev.addEventListener('sw:addedtowishlist', scheduleReconcile);
  }

  // Swym SDK 준비되면 콜백 (레포 표준 = SwymCallbacks, 폴링 X)
  function whenSwymReady(cb) {
    if (window._swat && window._swat.api && typeof window._swat.api.fetch === 'function') {
      cb();
      return;
    }
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(function () {
      cb();
    });
  }

  function init() {
    // 🔴 capture: true — Swym 자체 핸들러 + wishlist-open-on-add.js 보다 먼저 가로채기
    document.addEventListener(
      'click',
      function (e) {
        const btn = e.target.closest(BTN_SELECTOR);
        if (!btn) return;
        const container = btn.closest(CONTAINER_SELECTOR);
        if (!container) return;

        e.preventDefault();
        e.stopImmediatePropagation();

        if (btn.getAttribute('aria-pressed') === 'true') {
          handleRemove(btn, container);
        } else {
          handleAdd(btn, container);
        }
      },
      true
    );

    // 로드 시 하트 복원 (Swym 준비되면 1회) — /en 등 Swym 네이티브가 못 맞추는 케이스 보완
    whenSwymReady(hydrate);

    // 사이드바 drawer 의 remove/add 를 듣고 on-page 하트 양방향 re-sync (stale pressed 회귀 방지)
    whenSwymReady(bindWishlistEvents);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
